import { roleAtLeast, type Product } from '@ai-sales/shared'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState, type FormEvent } from 'react'
import { createProduct, deleteProduct, fetchProducts, updateProduct } from '../api/products.api.js'
import { Badge } from '../components/badge.js'
import { Button } from '../components/button.js'
import { ConfirmDialog } from '../components/confirm-dialog.js'
import { EmptyState } from '../components/data-table.js'
import { ErrorPanel } from '../components/error-panel.js'
import { Field } from '../components/field.js'
import { PageHeader, Panel } from '../components/page.js'
import { formatDateTime } from '../lib/format.js'
import { useCurrentWorkspace } from '../workspace/workspace-context.js'

/** Пустая форма: она же используется и для правки, поля те же. */
const EMPTY = {
  name: '',
  aliases: '',
  offerGroupId: '',
  summary: '',
  composition: '',
  benefits: '',
  price: '',
  delivery: '',
  note: '',
}

type FormValue = typeof EMPTY

function toForm(product: Product): FormValue {
  return {
    name: product.name,
    aliases: product.aliases.join(', '),
    offerGroupId: product.offerGroupId ?? '',
    summary: product.summary,
    composition: product.composition,
    benefits: product.benefits.join('\n'),
    price: product.price,
    delivery: product.delivery,
    note: product.note,
  }
}

/**
 * Список через запятую и список строками.
 *
 * Синонимы короткие — запятая читается. Плюсы бывают длинными, и запятая
 * внутри предложения разорвала бы их пополам, поэтому там каждая строка
 * отдельный пункт.
 */
const splitByComma = (value: string) =>
  value
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean)

const splitByLine = (value: string) =>
  value
    .split('\n')
    .map((item) => item.trim())
    .filter(Boolean)

export function ProductsPage() {
  const workspace = useCurrentWorkspace()
  const queryClient = useQueryClient()
  const canEdit = roleAtLeast(workspace.role, 'admin')

  const [form, setForm] = useState<FormValue | null>(null)
  const [editing, setEditing] = useState<Product | null>(null)
  const [removing, setRemoving] = useState<Product | null>(null)

  const products = useQuery({
    queryKey: ['products', workspace.id],
    queryFn: () => fetchProducts(workspace.id),
  })

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['products', workspace.id] })

  const save = useMutation({
    mutationFn: (value: FormValue) => {
      const payload = {
        name: value.name.trim(),
        aliases: splitByComma(value.aliases),
        offerGroupId: value.offerGroupId.trim(),
        summary: value.summary.trim(),
        composition: value.composition.trim(),
        benefits: splitByLine(value.benefits),
        price: value.price.trim(),
        delivery: value.delivery.trim(),
        note: value.note.trim(),
      }

      /**
       * Создание требует признака «работает», правка — нет: при правке
       * его меняет отдельная кнопка, и посылать его вместе с формой
       * значило бы каждый раз включать выключенный товар обратно.
       */
      return editing
        ? updateProduct(workspace.id, editing.id, payload)
        : createProduct(workspace.id, { ...payload, isActive: true })
    },
    onSuccess: async () => {
      setForm(null)
      setEditing(null)
      await invalidate()
    },
  })

  const toggle = useMutation({
    mutationFn: (product: Product) =>
      updateProduct(workspace.id, product.id, { isActive: !product.isActive }),
    onSuccess: invalidate,
  })

  const remove = useMutation({
    mutationFn: (product: Product) => deleteProduct(workspace.id, product.id),
    onSuccess: async () => {
      setRemoving(null)
      await invalidate()
    },
  })

  function submit(event: FormEvent) {
    event.preventDefault()

    if (form && form.name.trim() !== '') save.mutate(form)
  }

  const rows = products.data ?? []

  return (
    <>
      <PageHeader
        title="Товары"
        description="Что именно продаёт агент. Товар определяется по первому сообщению клиента и подставляется в инструкцию — целиком, как записан здесь."
        actions={
          canEdit && !form ? (
            <Button
              onClick={() => {
                setEditing(null)
                setForm(EMPTY)
              }}
            >
              Добавить товар
            </Button>
          ) : undefined
        }
      />

      <div className="flex flex-col gap-4">
        {products.error && (
          <ErrorPanel error={products.error} onRetry={() => void products.refetch()} />
        )}

        {form && (
          <Panel
            title={editing ? `Правка: ${editing.name}` : 'Новый товар'}
            description="Всё, что здесь написано, уходит в модель дословно. Пустое поле агент не выдумывает — он передаёт вопрос человеку."
          >
            <form onSubmit={submit} className="flex flex-col gap-3">
              <div className="grid gap-3 md:grid-cols-2">
                <Field
                  label="Название"
                  value={form.name}
                  required
                  hint="Ровно как на лендинге: по нему идёт сопоставление"
                  onChange={(event) => setForm({ ...form, name: event.target.value })}
                />
                <Field
                  label="Группа оффера"
                  value={form.offerGroupId}
                  hint="Цифры после дефиса в номере заявки. Сильнее названия"
                  onChange={(event) => setForm({ ...form, offerGroupId: event.target.value })}
                />
              </div>

              <Field
                label="Другие написания"
                value={form.aliases}
                hint="Через запятую. Нужны, когда на лендинге название пишут иначе"
                onChange={(event) => setForm({ ...form, aliases: event.target.value })}
              />

              <Field
                label="Что это"
                value={form.summary}
                hint="Одна строка простыми словами"
                onChange={(event) => setForm({ ...form, summary: event.target.value })}
              />

              <TextArea
                label="Состав"
                value={form.composition}
                hint="С упаковки. Пусто — агенту нечем ответить на вопрос о составе"
                onChange={(value) => setForm({ ...form, composition: value })}
              />

              <TextArea
                label="Чем помогает"
                value={form.benefits}
                hint="По одному пункту на строку. Два-три, без медицинских обещаний"
                onChange={(value) => setForm({ ...form, benefits: value })}
              />

              <div className="grid gap-3 md:grid-cols-2">
                <Field
                  label="Цена"
                  value={form.price}
                  hint="Как её называют клиенту: «Q269, прежняя Q538»"
                  onChange={(event) => setForm({ ...form, price: event.target.value })}
                />
                <Field
                  label="Доставка"
                  value={form.delivery}
                  hint="Сроки и города. Пусто — вопрос уйдёт в колл-центр"
                  onChange={(event) => setForm({ ...form, delivery: event.target.value })}
                />
              </div>

              <TextArea
                label="Ещё"
                value={form.note}
                hint="Акция, гарантия, особенности приёма"
                onChange={(value) => setForm({ ...form, note: value })}
              />

              {save.error && <ErrorPanel error={save.error} />}

              <div className="flex flex-wrap gap-2">
                <Button type="submit" loading={save.isPending}>
                  {editing ? 'Сохранить' : 'Добавить'}
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => {
                    setForm(null)
                    setEditing(null)
                  }}
                >
                  Отмена
                </Button>
              </div>
            </form>
          </Panel>
        )}

        {rows.length === 0 && !products.isLoading && !form && (
          <EmptyState
            title="Товаров пока нет"
            description="Пока каталог пуст, агент не назовёт клиенту ни одного товара: он не выдумывает то, чего ему не дали."
          />
        )}

        {rows.map((product) => (
          <Panel
            key={product.id}
            title={product.name}
            description={product.summary || 'описание не заполнено'}
            actions={
              <div className="flex flex-wrap items-center gap-2">
                <Badge tone={product.isActive ? 'ok' : 'neutral'}>
                  {product.isActive ? 'работает' : 'выключен'}
                </Badge>
                {product.source && <Badge tone="warn">из сервиса «{product.source}»</Badge>}
                {canEdit && !product.source && (
                  <>
                    <Button
                      variant="ghost"
                      onClick={() => {
                        setEditing(product)
                        setForm(toForm(product))
                      }}
                    >
                      Править
                    </Button>
                    <Button variant="ghost" onClick={() => toggle.mutate(product)}>
                      {product.isActive ? 'Выключить' : 'Включить'}
                    </Button>
                    <Button variant="ghost" onClick={() => setRemoving(product)}>
                      Удалить
                    </Button>
                  </>
                )}
              </div>
            }
          >
            {product.source && (
              /**
               * Кнопок правки у зеркального товара нет, и без объяснения
               * это выглядело бы поломкой. Сказано, где его править и
               * когда мы его последний раз видели, — второе отвечает на
               * вопрос «а синхронизация вообще работает?».
               */
              <p className="mb-3 rounded-md border border-copper/30 bg-copper-soft px-3 py-2 text-xs text-ink-soft">
                Товар приходит из сервиса «{product.source}» и правится там. У нас он только для
                чтения: правка всё равно исчезла бы при следующем обновлении каталога.
                {product.syncedAt && ` Последний раз получен ${formatDateTime(product.syncedAt)}.`}
              </p>
            )}

            <dl className="grid gap-x-6 gap-y-2 text-sm md:grid-cols-2">
              <Row label="Цена" value={product.price} />
              <Row label="Доставка" value={product.delivery} />
              <Row label="Состав" value={product.composition} />
              <Row label="Группа оффера" value={product.offerGroupId ?? ''} />
              <Row label="Другие написания" value={product.aliases.join(', ')} />
              <Row label="Чем помогает" value={product.benefits.join('; ')} />
            </dl>
          </Panel>
        ))}
      </div>

      <ConfirmDialog
        open={removing !== null}
        onOpenChange={(open) => !open && setRemoving(null)}
        title={`Удалить «${removing?.name ?? ''}»?`}
        description="Переписки, в которых он продавался, останутся — у них сохранятся и источник трафика, и название из первого сообщения. Исчезнет только карточка товара."
        confirmLabel="Удалить"
        danger
        loading={remove.isPending}
        onConfirm={() => removing && remove.mutate(removing)}
      />
    </>
  )
}

/**
 * Пустое поле показывается прямо, а не прочерком.
 *
 * «Не заполнено» — это то, что человек должен увидеть перед запуском:
 * именно об этом агент не сможет ответить клиенту.
 */
function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col gap-0.5">
      <dt className="label-xs">{label}</dt>
      <dd className={value ? 'text-ink' : 'text-copper'}>{value || 'не заполнено'}</dd>
    </div>
  )
}

function TextArea({
  label,
  value,
  hint,
  onChange,
}: {
  label: string
  value: string
  hint: string
  onChange: (value: string) => void
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <label className="text-sm font-medium text-ink-soft">
        {label}
        <textarea
          value={value}
          rows={3}
          onChange={(event) => onChange(event.target.value)}
          className="mt-1.5 w-full rounded-md border border-line bg-raised px-3 py-2 text-sm font-normal text-ink"
        />
      </label>
      <p className="text-xs text-muted">{hint}</p>
    </div>
  )
}
